export interface DashboardInputs {
  price: number
  productCost: number
  fleteInicial: number
  cancelledPct: number
  returnPct: number
  cpaIdeal: number
}

export interface DashboardMetrics {
  cpaIdeal: number
  cpaCosteado: number
  fleteReal: number
  grossMargin: number
  netProfit: number
  netMargin: number
  cpaBreakEven: number
  roasBreakEven: number
  roasTarget: number
  funnelOrders: number
  funnelDispatched: number
  funnelDelivered: number
  dispatchedPct: number
  efficiencyFinal: number
  cancelledPct: number
  returnPct: number
  trafficLightState: 'green' | 'yellow' | 'red'
}

export type CountryCode = 'CO' | 'MX' | 'EC' | 'AR'

export interface CountryConfig {
  code: CountryCode
  name: string
  currency: string
  symbol: string
  locale: string
  flag: string
}

export const COUNTRIES: CountryConfig[] = [
  {
    code: 'CO',
    name: 'Colombia',
    currency: 'COP',
    symbol: 'COP $',
    locale: 'es-CO',
    flag: '🇨🇴',
  },
  {
    code: 'MX',
    name: 'México',
    currency: 'MXN',
    symbol: 'MXN $',
    locale: 'es-MX',
    flag: '🇲🇽',
  },
  {
    code: 'EC',
    name: 'Ecuador',
    currency: 'USD',
    symbol: 'USD $',
    locale: 'en-US',
    flag: '🇪🇨',
  },
  {
    code: 'AR',
    name: 'Argentina',
    currency: 'ARS',
    symbol: 'ARS $',
    locale: 'es-AR',
    flag: '🇦🇷',
  },
]

export function getCountry(code: CountryCode): CountryConfig {
  return COUNTRIES.find((c) => c.code === code)!
}

export function calculateMetrics(inputs: DashboardInputs): DashboardMetrics {
  const {
    price,
    productCost,
    fleteInicial,
    cancelledPct,
    returnPct,
    cpaIdeal,
  } = inputs

  // FUNNEL: COD Funnel Logic
  const dispatchedPct = 100 - cancelledPct
  const efficiencyFinal = dispatchedPct * (1 - returnPct / 100)
  const efficiencyDecimal = efficiencyFinal / 100

  // Funnel numbers (base 100 orders)
  const funnelOrders = 100
  const funnelDispatched = Math.round(dispatchedPct)
  const funnelDelivered = Math.round(efficiencyFinal)

  // Flete Real = (Flete Inicial * % Despachados) / % Eficiencia Final
  const fleteReal =
    efficiencyFinal > 0 ? (fleteInicial * dispatchedPct) / efficiencyFinal : 0

  // Gross Margin
  const grossMargin = price - productCost - fleteReal

  // CPA Break-Even
  const cpaBreakEvenExact = grossMargin * efficiencyDecimal

  // ROAS Break-Even
  const roasBreakEvenExact =
    cpaBreakEvenExact > 0 ? price / cpaBreakEvenExact : 0

  // ROAS Target
  const roasTargetExact = cpaIdeal > 0 ? price / cpaIdeal : 0

  // CPA Costeado
  const cpaCosteadoExact =
    efficiencyDecimal > 0 ? cpaIdeal / efficiencyDecimal : 0

  // Net Profit
  const netProfit = price - productCost - fleteReal - cpaCosteadoExact

  // Net Margin
  const netMargin = price > 0 ? (netProfit / price) * 100 : 0

  // Traffic Light
  let trafficLightState: 'green' | 'yellow' | 'red'
  if (cpaIdeal < cpaBreakEvenExact * 0.6) {
    trafficLightState = 'green'
  } else if (cpaIdeal < cpaBreakEvenExact) {
    trafficLightState = 'yellow'
  } else {
    trafficLightState = 'red'
  }

  return {
    cpaIdeal,
    cpaCosteado: Math.round(cpaCosteadoExact),
    fleteReal: Math.round(fleteReal),
    grossMargin: Math.round(grossMargin),
    netProfit: Math.round(netProfit),
    netMargin: parseFloat(netMargin.toFixed(2)),
    cpaBreakEven: Math.round(cpaBreakEvenExact),
    roasBreakEven: parseFloat(roasBreakEvenExact.toFixed(2)),
    roasTarget: parseFloat(roasTargetExact.toFixed(2)),
    funnelOrders,
    funnelDispatched,
    funnelDelivered,
    dispatchedPct: parseFloat(dispatchedPct.toFixed(1)),
    efficiencyFinal: parseFloat(efficiencyFinal.toFixed(1)),
    cancelledPct,
    returnPct,
    trafficLightState,
  }
}

export function formatCurrency(
  value: number,
  countryCode: CountryCode = 'CO',
): string {
  const country = getCountry(countryCode)
  return country.symbol + Math.abs(value).toLocaleString(country.locale)
}

export function formatNumber(
  value: number,
  countryCode: CountryCode = 'CO',
): string {
  const country = getCountry(countryCode)
  return Math.abs(value).toLocaleString(country.locale)
}
